import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    TabsListFadeColorConstantType,
    TabsListDynamicStyleType
} from "./type";
import type {
    Mutable
} from "../../../../types";
import {
    getEdgeFadeColor
} from "../../../scrollArea/internal/edgeFade/stylesheet";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";
import {
    getHugStyle
} from "../../../../helpers/theme/spread";

export const getTabsListFadeColor = ({
    customFadeColor,
    colors
}: TabsListFadeColorConstantType): string => {
    return getEdgeFadeColor({
        fallbackColor: colors.content.container.default,
        customFadeColor,
        colors
    });
};

const stylesheet = StyleSheet.create({
    layer: {
        pointerEvents: "none",
        position: "absolute"
    },
    bar: {
        flexDirection: "row",
        alignItems: "center"
    },
    stretch: {
        alignSelf: "stretch"
    },
    overflowVisible: {
        overflow: "visible"
    },
    clipped: {
        overflow: "hidden"
    },
    baseline: {
        maxWidth: "100%"
    },
    verticalFrame: {
        maxWidth: "50%",
        flexShrink: 1,
        minWidth: 0
    },
    railVertical: {
        bottom: 0,
        start: 0,
        top: 0
    },
    scroll: {
        flexGrow: 0
    },
    unrevealed: {
        opacity: 0
    },
    span: {
        start: 0,
        end: 0
    }
});

export const useStyles = ({
    isOverflowVisible,
    isWidthControlled,
    currentMeasures,
    spreadBehaviour,
    isInsetApplied,
    shapeConfigs,
    orientation,
    isScroller,
    isClipped,
    userStyle,
    isHidden,
    borders,
    variant,
    colors,
    spaces
}: TabsListDynamicStyleType) => {
    const isSegment = variant === "segment";

    const isVertical = orientation === "vertical";

    const styles = {
        fade: {
            bottom: currentMeasures.reserveInset + borders.separator,
            top: currentMeasures.reserveInset,
            width: spaces.spacingXl
        } as Mutable<ViewStyle>,
        track: {
            backgroundColor: colors.content.container.raised,
            borderColor: colors.content.border.default,
            borderWidth: borders.line,
            ...getCornerStyle({
                radius: currentMeasures.trackRadius,
                shapeConfigs
            }),
            bottom: currentMeasures.reserveInset,
            top: currentMeasures.reserveInset
        } as Mutable<ViewStyle>,
        row: {
            flexDirection: isVertical ? "column" : "row",
            alignItems: isVertical ? "stretch" : "center"
        } as Mutable<ViewStyle>,
        rail: {
            backgroundColor: colors.content.border.subtle
        } as Mutable<ViewStyle>,
        scrollContent: {} as Mutable<ViewStyle>,
        scroll: {} as Mutable<ViewStyle>,
        frame: {} as Mutable<ViewStyle>,
        bar: {} as Mutable<ViewStyle>
    };

    if(isVertical) {
        Object.assign(styles.frame, stylesheet.verticalFrame);

        if(!isWidthControlled) {
            Object.assign(styles.frame, {
                width: shapeConfigs.sideListWidth
            });
        }
    } else {
        Object.assign(styles.frame, isSegment && spreadBehaviour === "baseline" ? stylesheet.baseline : stylesheet.stretch);

        if(isSegment && spreadBehaviour === "baseline") {
            Object.assign(styles.frame, getHugStyle({
                style: userStyle
            }));
        }
    }

    if(isVertical) {
        Object.assign(styles.rail, {
            width: borders.separator
        });

        Object.assign(styles.track, {
            bottom: 0,
            top: 0
        });
    } else {
        Object.assign(styles.rail, {
            bottom: currentMeasures.reserveInset,
            height: borders.separator
        });

        Object.assign(styles.row, {
            minHeight: currentMeasures.cellHeight
        });
    }

    if(isSegment) {
        Object.assign(styles.row, {
            paddingHorizontal: currentMeasures.inset,
            gap: spaces.spacingXxs
        });

        if(isVertical) {
            Object.assign(styles.row, {
                paddingVertical: currentMeasures.inset
            });
        }
    } else {
        Object.assign(styles.row, {
            gap: currentMeasures.lineGap
        });
    }

    if(isClipped) {
        Object.assign(styles.bar, stylesheet.clipped);
    }

    if(isHidden) {
        Object.assign(styles.bar, stylesheet.unrevealed);
    }

    if(isScroller) {
        Object.assign(styles.scroll, stylesheet.scroll);

        if(isOverflowVisible) {
            Object.assign(styles.scroll, stylesheet.overflowVisible);
        }

        if(isInsetApplied) {
            Object.assign(styles.scrollContent, {
                paddingHorizontal: spaces.spacingMd
            });
        }
    }

    return styles;
};
export default stylesheet;
